import {BREAK_BEGIN, BREAK_END, checkCanLogin, getShowOutOfWorkTimePrompt, PATROL_BEGIN, PATROL_END, WORK_BEGIN, WORK_END} from '@/library/modules/time-tracker/time-tracker';
import {Popup} from '@/library/popup';
import {importWrapper} from '@/library/utils';
import {deepCopyObject, errorHandler, isStaging, XHR_STATUS_DEFAULT} from '@/utils/portuna';
import axios, {AxiosError} from 'axios';
import {type ActionTree, type Commit, type Dispatch} from 'vuex';
import Loader from '../../../library/intranet/loader';
import LocalStorage from '../../../library/localStorage';
import TopMessageHandler from '../../../library/topMessageHandler';
import XHRManager from '../../../library/xhrManager';

const PlanningObligatoryPopup = importWrapper(() => import('../../../views/time-tracker/planning/PlanningObligatoryPopup.vue'));

let lastTimeUpdate                        = Date.now();
let popupResetTime                        = 300 * 1000; // time after popup appears again after the close
let updateTimeInterval                    = 0;
let lastEntryTime                         = 0;
let lastProjectTime                       = 0;
let actualBreakTime                       = 0;
let planningChecked                       = false;
let fetchPromise: Nullable<Promise<void>> = null;
const storageExcludeKeys                  = ['locked'];

function fetchCallback({commit, dispatch}: { dispatch: Dispatch; commit: Commit }, resolve: () => void, reject: (error: null | AxiosError) => void): XHRCallback
{
    return (error, data) =>
    {
        if (error)
        {
            reject(error);
            return console.error('[TimeTracker xhr error]', error);
        }

        lastTimeUpdate = Date.now();
        commit('setData', {
            lastUpdate: Date.now(),
            currentState: data.lastEntryType,
            currentCircumstance: data.currentCircumstance,
            lastEntryStampIntl: data.lastEntryStampIntl,
            homeOffice: data.isHomeOffice,
            patrolActive: data.patrolActive,
            actualBreakTimestamp: data.actualBreakTimestamp * 1000,
            workTime: data.workTimestamp * 1000,
            lastProjectWorkTime: data.lastProjectWorkTimestamp * 1000,
            allocatedWorkTime: data.allocatedWorkTime ? data.allocatedWorkTime : null,
            actualWorkTime: data.actualWorkTime ? data.actualWorkTime : null,
            workTimes: data.workTimes,
            breakTime: data.breakTimestamp * 1000,
            compensatedMinimumBreakTime: data.compensatedMinimumBreakTime,
            allocatedBreakTime: data.allocatedBreakTime,
            actualBreakTime: data.actualBreakTime ? data.actualBreakTime : null,
            breakTimes: data.breakTimes,
            checkIp: data.checkIp,
            startDate: data.startDate,
            workStart: data.workStart,
            breakNotice: data.breakNotice,
            isAbsent: data.isAbsent,
            hasProject: data.hasProject,
            hasContact: data.hasContact,
            lastProject: data.lastProject,
            lastContact: data.lastContact,
            lastContactName: data.lastContactName,
            projects: data.projects,
        });

        dispatch('startUpdateInterval');
        dispatch('checkObligatoryPlanning');
        dispatch('updateLocalStorage');

        resolve();
    };
}

const actions: ActionTree<TimeTrackerState, RootState> = {
    updateLocalStorage({state})
    {
        const data = deepCopyObject(state);
        for (let key of storageExcludeKeys)
        {
            delete data[key];
        }
        LocalStorage.set('timeTracker', data);
    },
    updateState({commit, dispatch}, currentState)
    {
        commit('setData', {currentState});
        if (currentState === BREAK_BEGIN)
        {
            dispatch('togglePopup', true);
            actualBreakTime = 0;
        }
        // otherwise update local storage data
        else
        {
            commit('setData', {popup: false});
        }
        dispatch('updateLocalStorage');
        dispatch('startUpdateInterval');
    },
    updateCircumstance({commit, dispatch}, currentCircumstance)
    {
        commit('setData', {currentCircumstance});

        dispatch('updateLocalStorage');
    },
    startUpdateInterval({commit, dispatch, getters, state, rootState})
    {
        if (updateTimeInterval)
        {
            window.clearInterval(updateTimeInterval);
        }

        // show popup if user is in break or logged out
        if ((getters.isBreak || getters.isLoggedOut) && Date.now() > state.popupReset)
        {
            if (!(rootState.personalDashboard as IPersonalDashboardState).isOpen)
            {
                dispatch('togglePopup', true);
            }
            else
            {
                if (isStaging())
                {
                    console.debug('[PersonalDashboardPopup] popup is open cannot show TimeTracker popup.');
                }
            }
        }
        else if (getters.isLoggedIn && state.popup)
        {
            dispatch('togglePopup', false);
        }

        if (getters.isLoggedIn)
        {
            lastEntryTime   = state.workTime;
            lastProjectTime = state.lastProjectWorkTime;
        }
        else if (getters.isBreak)
        {
            lastEntryTime   = state.breakTime;
            actualBreakTime = state.actualBreakTime ? state.actualBreakTime.value : 0;
        }
        else
        {
            return;
        }

        updateTimeInterval = window.setInterval(() =>
        {
            if (!getters.isLoggedIn && !getters.isBreak)
            {
                window.clearInterval(updateTimeInterval);
            }

            // difference between now and last time update
            let timeChange = Date.now() - lastTimeUpdate;
            lastTimeUpdate = Date.now();

            // if user is in break then add timeChange to currentBreakTime
            if (getters.isBreak)
            {
                actualBreakTime += timeChange;
                if (actualBreakTime % 60_000 < 1_000)
                {
                    commit('setData', {actualBreakTime});
                }
            }

            // add timeChange to lastEntryTime (work or break time)
            lastEntryTime += timeChange;
            lastProjectTime += timeChange;

            // if condition prevent a vue render update every second
            if (lastEntryTime % 60_000 < 1_000)
            {
                commit('setData', {[getters.isLoggedIn ? 'workTime' : 'breakTime']: lastEntryTime});

                if (getters.isLoggedIn)
                {
                    commit('setData', {lastProjectWorkTime: lastProjectTime});
                }
            }

            if (getters.isLoggedIn && lastProjectTime % 60_000 < 1_000)
            {
                commit('setData', {lastProjectWorkTime: lastProjectTime});
            }
        }, 1000);
    },
    fetch({commit, dispatch, state}): Promise<void>
    {
        if (fetchPromise)
        {
            return fetchPromise;
        }

        // if not initialized add event listener for timeTracker local storage data
        LocalStorage.on('timeTracker', () =>
        {
            let data = LocalStorage.get('timeTracker');

            if (typeof data === 'object' && data)
            {
                commit('setData', data);
            }
        });

        fetchPromise = new Promise((resolve, reject) => {
            XHRManager.add(1, 'timeTracker', {
                send: true,
                ignoreLocalStorage: false,
            }, fetchCallback({commit, dispatch}, resolve, reject));
        });

        const timeTrackerStorageData = LocalStorage.get('timeTracker', {});
        for (let key of storageExcludeKeys)
        {
            delete timeTrackerStorageData[key];
        }

        commit('setData', timeTrackerStorageData);
        dispatch('startUpdateInterval');

        return fetchPromise;
    },
    refresh({commit, dispatch, state}): Promise<void>
    {
        return new Promise((resolve, reject) => {
            XHRManager.sendCallback('timeTracker', fetchCallback({commit, dispatch}, resolve, reject));
        });
    },
    track({
        commit,
        dispatch,
        state,
        rootState,
        getters,
    }, {
        record,
        projectId,
        contactId,
    }): Promise<boolean>
    {
        if (state.locked)
        {
            TopMessageHandler.error('Die vorausgegangene Aktion ist noch nicht abgeschlossen!');
            return Promise.resolve(false);
        }

        if (['home', 'workbegin'].includes(record))
        {
            if (state.hasProject && !projectId && checkCanLogin())
            {
                if (state.projects.length)
                {
                    TopMessageHandler.error('Wählen Sie zunächst ein Projekt aus.');
                }
                else
                {
                    TopMessageHandler.error('Legen Sie zunächst ein Projekt an.');
                }

                dispatch('setPopupProjectPrompt', true);
                dispatch('setPopup', true);
                commit('personalDashboard/setOpen', false);

                return Promise.resolve(false);
            }

            if (state.hasContact && !contactId && checkCanLogin())
            {
                TopMessageHandler.error('Wählen Sie zunächst einen Kontakt aus.');

                dispatch('setPopupProjectPrompt', true);
                dispatch('setPopup', true);
                commit('personalDashboard/setOpen', false);

                return Promise.resolve(false);
            }

            if (!state.hasProject && !state.hasContact && (!state.popupOutOfWorkTimePrompt || !state.popup) && getShowOutOfWorkTimePrompt())
            {
                dispatch('setPopupOutOfWorkTimePrompt', true);
                dispatch('setPopup', true);

                return Promise.resolve(false);
            }

            dispatch('setPopupOutOfWorkTimePrompt', false);
        }

        commit('setLocked', true);

        let type = null;
        if (record === 'home')
        {
            record = 'workbegin';
            type   = 'home';
        }

        if (record === 'patrolbegin')
        {
            record = 'workbegin';
            type   = 'patrol';
        }

        if (record === 'patrolend')
        {
            record = 'workend';
            type   = 'patrol';
        }

        let loaderMessage = '';
        if (record === 'workbegin' && type !== 'patrol')
        {
            if (getters.isLoggedIn)
            {
                TopMessageHandler.error('Sie haben sich nicht zur Arbeit anmelden, da Sie bereits zur Arbeit angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }

            loaderMessage = 'Sie werden zur Arbeit angemeldet...';
        }
        else if (record === 'workbegin' && type === 'patrol')
        {
            if (!getters.isLoggedIn)
            {
                TopMessageHandler.error('Sie können sich nicht zum Dienstgang anmelden, da Sie nicht zur Arbeit angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }
            if (getters.isBreak)
            {
                TopMessageHandler.error('Sie können sich nicht zum Dienstgang anmelden, da Sie zur Pause angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }
            if (getters.isOnPatrol)
            {
                TopMessageHandler.error('Sie können sich nicht zum Dienstgang anmelden, da Sie bereits zum Dienstgang angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }

            loaderMessage = 'Sie werden zum Dienstgang angemeldet...';
        }
        else if (record === 'breakbegin')
        {
            if (getters.isBreak)
            {
                TopMessageHandler.error('Sie können sich nicht zur Pause abmelden, da Sie sich bereits in der Pause befinden!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }
            else if (getters.isLoggedOut)
            {
                TopMessageHandler.error('Sie können sich nicht zur Pause abmelden, da Sie nicht zur Arbeit angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }
            if (getters.isOnPatrol)
            {
                TopMessageHandler.error('Sie können sich nicht zur Pause abmelden, da Sie noch zum Dienstgang angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }

            loaderMessage = 'Sie werden zur Pause angemeldet...';
        }
        else if (record === 'workend' && type !== 'patrol')
        {
            if (getters.isLoggedOut || getters.isBreak)
            {
                TopMessageHandler.error('Sie können sich nicht von der Arbeit abmelden, da Sie nicht zur Arbeit angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }

            if (getters.isOnPatrol)
            {
                TopMessageHandler.error('Sie können sich nicht von der Arbeit abmelden, da Sie zum Dienstgang angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }

            loaderMessage = 'Sie werden von der Arbeit abgemeldet...';
        }
        else if (record === 'workend' && type === 'patrol')
        {
            if (!getters.isOnPatrol)
            {
                TopMessageHandler.error('Sie können sich nicht vom Dienstgang abmelden, da Sie nicht zum Dienstgang angemeldet sind!');
                commit('setLocked', false);
                return Promise.resolve(false);
            }

            if (getters.isBreak)
            {
                TopMessageHandler.error('Sie können sich nicht vom Dienstgang abmelden, da Sie zur Pause angemeldet sind');
                commit('setLocked', false);
                return Promise.resolve(false);
            }

            loaderMessage = 'Sie werden vom Dienstgang abgemeldet...';
        }

        Loader.show(loaderMessage);

        let params = '/record/' + record;

        if (type)
        {
            params += '/type/' + type;
        }

        if (state.hasProject && projectId)
        {
            params += '/project/' + projectId;
        }

        if (state.hasContact && contactId)
        {
            params += '/contact/' + contactId;
        }

        return new Promise((resolve, reject) => {
            axios.post('/employee/time-tracker/index/record' + params).then(response =>
            {
                if (response.data.status !== XHR_STATUS_DEFAULT)
                {
                    throw response;
                }

                const data = response.data.data;

                if ('projects' in data)
                {
                    commit('setData', {
                        hasProject: data.hasProject,
                        hasContact: data.hasContact,
                        lastProject: data.lastProject,
                        lastContact: data.lastContact,
                        lastContactName: data.lastContactName,
                        lastProjectWorkTime: data.lastProjectWorkTimestamp * 1000,
                        projects: data.projects,
                        contacts: data.contacts,
                    });
                }

                if (record === 'workbegin' && type !== 'patrol')
                {
                    dispatch('updateState', WORK_BEGIN);
                    TopMessageHandler.success('Sie haben sich erfolgreich zur Arbeit angemeldet.');

                    // check obligatory planning after work-begin
                    planningChecked = false;
                    dispatch('checkObligatoryPlanning');
                }
                else
                {
                    if (record === 'workbegin' && type === 'patrol')
                    {
                        if (data.lastEntryStampIntl)
                        {
                            commit('setData', {lastEntryStampIntl: data.lastEntryStampIntl});
                        }

                        dispatch('updateCircumstance', PATROL_BEGIN);
                        TopMessageHandler.success('Sie haben sich erfolgreich zum Dienstgang angemeldet.');
                    }
                    else if (record === 'breakbegin')
                    {
                        // set internationalized entry stamp string for current break-time calculation
                        if (data.lastEntryStampIntl)
                        {
                            commit('setData', {lastEntryStampIntl: data.lastEntryStampIntl});
                        }

                        dispatch('updateState', BREAK_BEGIN);
                        TopMessageHandler.success('Sie haben sich erfolgreich zur Pause angemeldet.');
                    }
                    else if (record === 'workend' && type !== 'patrol')
                    {
                        dispatch('updateState', WORK_END);
                        TopMessageHandler.success('Sie haben sich erfolgreich von der Arbeit abgemeldet.');
                    }
                    else if (record === 'workend' && type === 'patrol')
                    {
                        dispatch('updateCircumstance', PATROL_END);
                        TopMessageHandler.success('Sie haben sich erfolgreich von dem Dienstgang abgemeldet.');
                    }
                    else if (record === 'changeproject')
                    {
                        dispatch('updateState', WORK_BEGIN);
                        TopMessageHandler.success('Das Projekt wurde gewechselt');
                    }
                }

                resolve(true);
            })
            .catch(error => {
                reject(error);
                errorHandler(error);
            })
            .finally(() => {
                Loader.hide();

                window.setTimeout(() => {
                    commit('setLocked', false);
                }, 1000);
            });
        });
    },
    togglePopup({commit, dispatch, state, rootState}, value)
    {
        if (!value || value && (1 === state.checkIp || state.homeOffice) && !state.isAbsent)
        {
            dispatch('setPopup', value);
        }
    },
    setPopup({commit}, value)
    {
        commit('setData', {
            popup: value,
            popupReset: Date.now() + popupResetTime,
        });
    },
    checkObligatoryPlanning({commit, dispatch, state, rootState})
    {
        if(!planningChecked)
        {
            planningChecked = true;
            if(WORK_BEGIN === state.currentState || BREAK_END === state.currentState)
            {
                axios.get('/employee/time-tracker/planning/check-obligatory')
                .then(({data: {status, data: r}}) => {
                    if (status === XHR_STATUS_DEFAULT)
                    {
                        if(r.obligatory)
                        {
                            dispatch('toggleObligatoryPopup', r)
                        }
                    }
                    else
                    {
                        errorHandler(r.data);
                    }
                })
            }
        }
    },
    toggleObligatoryPopup({commit, dispatch, state, rootState}, data)
    {
        PlanningObligatoryPopup().then((Component) => {
            let popup = new Popup({
                title: `Fehlende geplante Arbeitszeiten`,
                titleIcon: 'info',
                size: 'long',
                component: Component.default,
                componentProps: {
                    weeks: data.weeks,
                    start: data.start,
                    end: data.end,
                },
                closeable: false,
            });

            popup.open();
        });
    },
    setPopupProjectPrompt({commit}, byProject: boolean)
    {
        commit('setData', {popupProjectPrompt: byProject});
    },
    setPopupOutOfWorkTimePrompt({commit}, value: boolean)
    {
        commit('setData', {popupOutOfWorkTimePrompt: value});
    },
};

export default actions;